import React, { useEffect, useState } from 'react';
import { Activity, BellRing, Building, CreditCard, Settings, Smartphone, Users } from 'lucide-react';
import UserManagement from './usermgmt';
import AgencySetup from './AgencySetup';
import PatientAdmin from './patientadmin';
import PatientAddPage from './patients/PatientAddPage';
import PatientEditPage from './patients/PatientEditPage';
import SystemConfig from '../config/SystemConfig';
import OrganizationAccount from './OrganizationAccount';
import NotificationSettings from './NotificationSettings';
import AdminBottomNav from '../layout/AdminBottomNav';
import { useMobileDetection } from '../../hooks/useMobileUtils';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { createPermissionString as perm } from '../../types/permissions';
import AdminDevices from './offline/AdminDevices';

type AdminTab = 'userMgmt' | 'patientAdmin' | 'agencySetup' | 'systemConfig' | 'organizationAccount' | 'offlineDevices' | 'notificationSettings';

interface AdminHomeProps {
  activeAdminTab: string | null;
  setActiveAdminTab: (tab: string) => void;
  sidebarCollapsed?: boolean;
  patientAdminMode: 'list' | 'add' | 'edit';
  setPatientAdminMode: (mode: 'list' | 'add' | 'edit') => void;
  patientEditId: string | null;
  setPatientEditId: (id: string | null) => void;
}

const AdminHome: React.FC<AdminHomeProps> = ({
  activeAdminTab,
  setActiveAdminTab,
  sidebarCollapsed = true,
  patientAdminMode,
  setPatientAdminMode,
  patientEditId,
  setPatientEditId
}) => {
  const isMobile = useMobileDetection();
  const canReadConfig = useCanPerform('read:config');
  // Phase 16C C11: offline devices and quarantine
  const canSeeDevices = useHasAnyPermission([perm('admin:offline:devices:read'), perm('admin:offline:quarantine:review')]);
  const canManageNotifications = useCanPerform('manage:notificationsettings');
  const [activeTab, setActiveTab] = useState<AdminTab>(
    isAdminTab(activeAdminTab) ? activeAdminTab : 'userMgmt'
  );
  useEffect(() => {
    if (isAdminTab(activeAdminTab)) {
      setActiveTab(activeAdminTab);
    }
  }, [activeAdminTab]);

  const selectTab = (tab: AdminTab) => {
    setActiveTab(tab);
    setActiveAdminTab(tab);
  };

  const renderPatientPage = () => {
    if (patientAdminMode === 'add') {
      return (
        <PatientAddPage
          onSuccess={() => {
            setPatientAdminMode('list');
            setPatientEditId(null);
          }}
          onCancel={() => {
            setPatientAdminMode('list');
            setPatientEditId(null);
          }}
        />
      );
    }
    if (patientAdminMode === 'edit' && patientEditId) {
      return (
        <PatientEditPage
          patientId={patientEditId}
          onSuccess={() => {
            setPatientAdminMode('list');
            setPatientEditId(null);
          }}
          onCancel={() => {
            setPatientAdminMode('list');
            setPatientEditId(null);
          }}
        />
      );
    }
    return (
      <PatientAdmin
        onNavigate={(action, patientId) => {
          if (action === 'add') {
            setPatientAdminMode('add');
          } else if (patientId) {
            setPatientAdminMode('edit');
            setPatientEditId(patientId);
          }
        }}
      />
    );
  };

  const renderTabContent = (tab: AdminTab, expanded: boolean, toggle: () => void) => {
    switch (tab) {
      case 'userMgmt':
        return <UserManagement expanded={expanded} toggleExpanded={toggle} />;
      case 'patientAdmin':
        return renderPatientPage();
      case 'agencySetup':
        return <AgencySetup />;
      case 'systemConfig':
        return <SystemConfig />;
      case 'organizationAccount':
        return <OrganizationAccount />;
      case 'offlineDevices':
        return <AdminDevices />;
      case 'notificationSettings':
        return canManageNotifications ? <NotificationSettings /> : null;
    }
  };

  const tabs = [
    { name: 'userMgmt' as const, label: 'Users', icon: Users },
    { name: 'patientAdmin' as const, label: 'Patients', icon: Activity },
    { name: 'agencySetup' as const, label: 'Agency Setup', icon: Building },
    ...(canReadConfig ? [
      { name: 'systemConfig' as const, label: 'System Config', icon: Settings },
      { name: 'organizationAccount' as const, label: 'Organization Account', icon: CreditCard }
    ] : []),
    ...(canSeeDevices ? [{ name: 'offlineDevices' as const, label: 'Devices', icon: Smartphone }] : []),
    // Phase 16F (REQ-NTF-06): routed roles per critical trigger, missed clock-in grace
    ...(canManageNotifications ? [{ name: 'notificationSettings' as const, label: 'Notifications', icon: BellRing }] : [])
  ];

  return (
    <div className={isMobile ? 'p-0' : 'p-4'}>
      {isMobile ? (
        <div className="space-y-3 pt-3 pb-20 px-3">
          <div className="rounded-lg shadow-md overflow-hidden">
            {renderTabContent(activeTab, true, () => undefined)}
          </div>
          <AdminBottomNav
            activeTab={activeTab}
            onTabChange={selectTab}
            sidebarCollapsed={sidebarCollapsed}
          />
        </div>
      ) : (
        <div className="mb-4">
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-2 pt-2 pb-0">
              <div className="flex space-x-1" role="tablist" aria-label="Administration">
                {tabs.map(({ name, label, icon: Icon }) => (
                  <button
                    key={name}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === name}
                    onClick={() => selectTab(name)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-md transition-all duration-200 flex items-center space-x-2 ${
                      activeTab === name
                        ? 'bg-slate-800 text-white'
                        : 'bg-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                    }`}
                  >
                    <Icon size={16} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div key={activeTab} role="tabpanel">
            {renderTabContent(activeTab, true, () => undefined)}
          </div>
        </div>
      )}
    </div>
  );
};

function isAdminTab(tab: string | null): tab is AdminTab {
  return tab === 'userMgmt' || tab === 'patientAdmin' || tab === 'agencySetup' || tab === 'systemConfig' || tab === 'organizationAccount' || tab === 'offlineDevices' || tab === 'notificationSettings';
}

export default AdminHome;